import dayjs from 'dayjs';

import { Roles, BadgeColor, BackOfficeRoles } from 'types';
import type {
  TimeOffSms,
  TimeOffRow,
  TimeOffBlock,
} from 'store/api/time-off/types';

// TIME OFF (owner 24 Oct 2026; plan TIME-OFF-BUILD-PLAN-v2.md §5, §6). Days
// are plain YYYY-MM-DD strings, never converted to UTC. The short range
// matches the server's timeOffText.js (tested on the same cases).

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const parts = (day?: string | null) => {
  const match = DAY_PATTERN.exec(String(day ?? ''));
  return match
    ? {
        year: Number(match[1]),
        date: Number(match[3]),
        month: Number(match[2]),
      }
    : null;
};

/** A local Date for a day string (never `new Date('YYYY-MM-DD')`: UTC). */
export const dayToDate = (day?: string | null): Date | null => {
  const p = parts(day);
  return p ? new Date(p.year, p.month - 1, p.date) : null;
};

/** A Date (local) as a day string. */
export const dateToDay = (date?: Date | null): string | null =>
  date && !Number.isNaN(date.getTime())
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    : null;

/**
 * Any date the app holds (a Date, 'YYYY-MM-DD', 'MM/DD/YYYY') as a day
 * string, or null when blank or unreadable. Blank first: formatDate would
 * turn undefined into today.
 */
export const toDay = (value?: string | Date | null): string | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'string' && DAY_PATTERN.test(value)) {
    return value;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

/** Today in New York, the one "today" time off uses (as the server). */
export const newYorkToday = (now: Date = new Date()): string =>
  new Intl.DateTimeFormat('en-CA', {
    day: '2-digit',
    year: 'numeric',
    month: '2-digit',
    timeZone: 'America/New_York',
  }).format(now);

/** Calendar days from start to end, both included. */
export const dayCount = (start: string, end: string): number => {
  const a = parts(start);
  const b = parts(end);
  if (!a || !b) {
    return 0;
  }
  const ms =
    Date.UTC(b.year, b.month - 1, b.date) -
    Date.UTC(a.year, a.month - 1, a.date);
  return Math.round(ms / 86_400_000) + 1;
};

export const daysText = (count: number): string =>
  count === 1 ? '1 day' : `${count} days`;

/** "Nov 12", "Nov 12–14", "Nov 30–Dec 2", "Dec 30, 2026–Jan 2, 2027". */
export const shortRange = (
  start?: string | null,
  end: string | null = start ?? null
) => {
  const a = parts(start);
  const b = parts(end);
  if (!a || !b) {
    return '';
  }
  const aMonth = MONTHS[a.month - 1];
  const bMonth = MONTHS[b.month - 1];
  if (start === end) {
    return `${aMonth} ${a.date}`;
  }
  if (a.year !== b.year) {
    return `${aMonth} ${a.date}, ${a.year}–${bMonth} ${b.date}, ${b.year}`;
  }
  if (a.month === b.month) {
    return `${aMonth} ${a.date}–${b.date}`;
  }
  return `${aMonth} ${a.date}–${bMonth} ${b.date}`;
};

/**
 * The range in lists and dialogs: "Nov 12 – Nov 14, 2026", one day
 * "Nov 12, 2026". `dropYear`: the Dashboard leaves out the current year.
 */
export const longRange = (
  start?: string | null,
  end: string | null = start ?? null,
  { dropYear = false, now = new Date() } = {}
) => {
  const a = parts(start);
  const b = parts(end);
  if (!a || !b) {
    return '';
  }
  const thisYear = Number(newYorkToday(now).slice(0, 4));
  const yearOf = (year: number) =>
    dropYear && year === thisYear ? '' : `, ${year}`;
  const first = `${MONTHS[a.month - 1]} ${a.date}`;
  const last = `${MONTHS[b.month - 1]} ${b.date}`;
  if (start === end) {
    return `${first}${yearOf(a.year)}`;
  }
  if (a.year !== b.year) {
    return `${first}, ${a.year} – ${last}, ${b.year}`;
  }
  return `${first} – ${last}${yearOf(b.year)}`;
};

/** "Oct 24" from an ISO time, in New York. */
export const shortDay = (iso?: string | null) => {
  if (!iso) {
    return '';
  }
  const day = newYorkToday(new Date(iso));
  return shortRange(day, day);
};

export const rangesOverlap = (
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string
) => aStart <= bEnd && bStart <= aEnd;

/** The blocks a picked range touches (react-datepicker lets a range span them). */
export const blockedOverlaps = (
  startDate: string | null,
  endDate: string | null,
  blocks: TimeOffBlock[] = []
) =>
  startDate && endDate
    ? blocks.filter((block) =>
        rangesOverlap(startDate, endDate, block.startDate, block.endDate)
      )
    : [];

/** "Nov 13–15 (Holiday Market), Dec 26". */
export const blocksText = (blocks: TimeOffBlock[]) =>
  blocks
    .map((block) =>
      block.label?.trim()
        ? `${shortRange(block.startDate, block.endDate)} (${block.label.trim()})`
        : shortRange(block.startDate, block.endDate)
    )
    .join(', ');

/** react-datepicker's excludeDateIntervals for the blocks (local Dates). */
export const blockIntervals = (blocks: TimeOffBlock[] = []) =>
  blocks
    .map((block) => ({
      end: dayToDate(block.endDate),
      start: dayToDate(block.startDate),
    }))
    .filter((interval): interval is { end: Date; start: Date } =>
      Boolean(interval.start && interval.end)
    );

export type TimeOffChip =
  | 'pending'
  | 'approved'
  | 'someDays'
  | 'cancelled'
  | 'notApproved'
  | 'notAnswered';

/** Which chip a row shows (Not answered: Pending, its dates passed). */
export const timeOffChip = (
  row: Pick<TimeOffRow, 'status' | 'endDate' | 'isPartial'>,
  today: string = newYorkToday()
): TimeOffChip => {
  if (row.status === 'pending') {
    return row.endDate < today ? 'notAnswered' : 'pending';
  }
  if (row.status === 'approved') {
    return row.isPartial ? 'someDays' : 'approved';
  }
  return row.status;
};

export const TIME_OFF_CHIP: Record<
  TimeOffChip,
  { text: string; color: BadgeColor }
> = {
  pending: { text: 'Pending', color: BadgeColor.TetrieryBlue },
  notApproved: { text: 'Not approved', color: BadgeColor.Red },
  cancelled: { text: 'Cancelled', color: BadgeColor.LightGrey },
  someDays: { color: BadgeColor.Yellow, text: 'Approved some days' },
  notAnswered: { text: 'Not answered', color: BadgeColor.LightGrey },
  approved: { text: 'Approved', color: BadgeColor.SecondaryPigmentIndigo },
};

/** The rep's "Decision" cell. */
export const decisionText = (row: TimeOffRow, today?: string): string => {
  const chip = timeOffChip(row, today);
  if (chip === 'pending' || chip === 'notAnswered') {
    return `Asked ${shortDay(row.createdAt)}`;
  }
  if (chip === 'cancelled') {
    return `Cancelled ${shortDay(row.cancelledAt)}`;
  }
  if (chip === 'notApproved') {
    return `Reason: "${row.decisionNote ?? ''}"`;
  }
  if (chip === 'someDays') {
    const note = row.decisionNote ? ` · ${row.decisionNote}` : '';
    const approved = longRange(row.approvedStartDate, row.approvedEndDate);
    // The year is left out only when both days are in the same year.
    const sameYear =
      row.approvedStartDate?.slice(0, 4) === row.approvedEndDate?.slice(0, 4);
    return `Approved ${sameYear ? approved.replace(/, \d{4}$/, '') : approved} (you asked ${shortRange(row.startDate, row.endDate)})${note}`;
  }
  return row.decidedByName ? `Approved by ${row.decidedByName}` : 'Approved';
};

/**
 * The profile's Time off tab: on the own profile for a role that asks, on an
 * employee's profile for the approvers.
 */
export const showTimeOffTab = (
  isEmployee: boolean | undefined,
  settings?: { canRequest?: boolean; isApprover?: boolean }
) => Boolean(isEmployee ? settings?.isApprover : settings?.canRequest);

/**
 * Whether the approver has any icon on this row. Without one the row leaves
 * the actions cell out: on a tablet or phone card an empty cell still adds a
 * row gap under the last line (Dev pass 24 Oct).
 */
export const hasApproverActions = ({ can }: Pick<TimeOffRow, 'can'>) =>
  can.approve ||
  can.approveSomeDays ||
  can.decline ||
  can.editDecision ||
  can.delete;

/** The later of two days (e.g. a request's first day and today). */
export const laterDay = (a: string, b: string) => (a > b ? a : b);

/** Company Owner, Administrative and Admin Rep decide time off (the server checks too). */
export const isTimeOffApproverRole = (role?: string | null) =>
  role === Roles.COMPANY_OWNER ||
  role === BackOfficeRoles.ADMINISTRATIVE ||
  role === BackOfficeRoles.ADMIN_REP;

/** The toast after a decision, from what the server did with the text. */
export const decisionToast = (
  kind: 'edit' | 'approved' | 'notApproved' | 'approvedSomeDays',
  sms: TimeOffSms,
  name?: string | null
) => {
  const lead = {
    edit: 'Decision saved',
    approved: 'Time off approved',
    notApproved: 'Time off declined',
    approvedSomeDays: 'Time off approved for some days',
  }[kind];
  const who = name?.trim() || 'The rep';
  if (sms === 'noPhone') {
    return `${lead} · ${who} has no phone on file, so no text was sent`;
  }
  if (sms === 'notSent') {
    return `${lead} · the text could not be sent`;
  }
  return `${lead} · ${who} gets a text`;
};
